import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import DashboardLayout from "../dashboard/DashboardLayout";
import React from 'react';
import {
  Play,
  Pause,
  RotateCcw,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  Save,
  Pencil,
  MapPin,
  Zap,
  Building2,
  BadgeCheck,
  AlertTriangle,
  XCircle,
  Circle,
  CircleDot,
  PauseCircle,
  CheckCircle2,
  Plus,
  Info,
  ChevronRight,
  TrendingUp,
  Target,
  Cpu,
  RefreshCw,
  ExternalLink,
  Upload,
  File,
  X,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { trackPageView, trackButtonClick, trackEvent } from "../../utils/analytics";

const getAgentLimit = (planId) => {
  if (!planId) return 0;
  if (planId === "pro") return 2;
  if (planId === "elite") return 5;
  return 0;
};

const getUserPlan = () => {
  try {
    return localStorage.getItem("applyease_selected_plan") || null;
  } catch {
    return null;
  }
};

const getDummyResumes = () => [
  { id: "resume-demo-1", title: "Software Engineer CV (Demo)", is_active: true },
  { id: "resume-demo-2", title: "Frontend Specialist CV (Demo)", is_active: false },
];

const JobAgent = () => {
  const navigate = useNavigate();
  const [resumes, setResumes] = useState([]);
  const [selectedResumeId, setSelectedResumeId] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [location, setLocation] = useState("");
  
  // New State Variables
  const [salaryRange, setSalaryRange] = useState("");
  const [expectedSalary, setExpectedSalary] = useState("");
  const [locationPreference, setLocationPreference] = useState("");
  const [autoApply, setAutoApply] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]); // [{name, size, type}]

  const [jobsPerDay, setJobsPerDay] = useState(15);
  const [agentStatus, setAgentStatus] = useState("idle");
  const [history, setHistory] = useState([]);
  const [agentConfigs, setAgentConfigs] = useState([]);
  const [activeAgentId, setActiveAgentId] = useState("");
  const [activeTab, setActiveTab] = useState("active"); // active | archived
  const [editingAgentId, setEditingAgentId] = useState("");
  const [editingDraft, setEditingDraft] = useState({
    title: "",
    location: "",
    jobsPerDay: 15,
    resumeId: "",
    jobDescription: "",
    salaryRange: "",
    expectedSalary: "",
    locationPreference: "",
    autoApply: false,
    attachedFiles: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [userPlan, setUserPlan] = useState(getUserPlan());
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [subscription, setSubscription] = useState(null);

  const activeAgent = agentConfigs.find((a) => a.id === activeAgentId) || null;

  const handleSetActiveAgent = (agent) => {
    if (!agent?.id) return;
    setIsCreatingNew(false);
    setActiveAgentId(agent.id);
    setJobTitle(agent.title || "");
    setLocation(agent.location || "");
    if (typeof agent.jobsPerDay === "number") setJobsPerDay(agent.jobsPerDay);
    if (agent.resumeId) setSelectedResumeId(agent.resumeId);
    if (typeof agent.jobDescription === "string") setJobDescription(agent.jobDescription);
    if (typeof agent.status === "string") setAgentStatus(agent.status);
    
    // Set New Fields
    setSalaryRange(agent.salary_range || "");
    setExpectedSalary(agent.expected_salary || "");
    setLocationPreference(agent.location_preference || "");
    setAutoApply(agent.auto_apply || false);
    setAttachedFiles(agent.attached_files || []);
  };

  const handleEditAgent = (agent) => {
    if (!agent?.id) return;
    setEditingAgentId(agent.id);
    setEditingDraft({
      title: agent.title || "",
      location: agent.location || "",
      jobsPerDay: typeof agent.jobsPerDay === "number" ? agent.jobsPerDay : 15,
      resumeId: agent.resumeId || "",
      jobDescription: agent.jobDescription || "",
      salaryRange: agent.salary_range || "",
      expectedSalary: agent.expected_salary || "",
      locationPreference: agent.location_preference || "",
      autoApply: agent.auto_apply || false,
      attachedFiles: agent.attached_files || [],
    });
  };

  const handleCancelEditAgent = () => {
    setEditingAgentId("");
    setEditingDraft({ 
      title: "", location: "", jobsPerDay: 15, resumeId: "", jobDescription: "",
      salaryRange: "", expectedSalary: "", locationPreference: "", autoApply: false, attachedFiles: []
    });
  };

  const handleUpdateAgent = async () => {
    setError("");
    setSuccess("");
    if (!editingAgentId) return;
    if (!editingDraft.title.trim()) return setError("Agent title is required.");
    const resumeTitle = resumes.find((r) => r.id === editingDraft.resumeId)?.title || "Selected CV";
    setLoading(true);
    try {
      await axiosInstance.patch(`/job-agents/${editingAgentId}`, {
        title: editingDraft.title.trim(),
        location: editingDraft.location.trim() || null,
        jobs_per_day: editingDraft.jobsPerDay,
        resume_id: editingDraft.resumeId,
        resume_title: resumeTitle,
        job_description: editingDraft.jobDescription || null,
        salary_range: editingDraft.salaryRange || null,
        expected_salary: editingDraft.expectedSalary || null,
        location_preference: editingDraft.locationPreference || null,
        auto_apply: editingDraft.autoApply,
        attached_files: editingDraft.attachedFiles,
      });
      await loadAgentConfigs();
      setSuccess("Agent updated");
      setTimeout(() => setSuccess(""), 2000);
      handleCancelEditAgent();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to update agent");
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveAgent = async (e, agentId) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to archive this agent?")) return;
    try {
      await axiosInstance.delete(`/job-agents/${agentId}`);
      setSuccess("Agent Archived");
      await loadAgentConfigs();
      if (activeAgentId === agentId) {
        setActiveAgentId("");
        setIsCreatingNew(true);
      }
      setTimeout(() => setSuccess(""), 2000);
    } catch (err) {
      setError("Failed to archive agent");
    }
  };

  const formatRelativeTime = (iso) => {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    const diffMs = Date.now() - t;
    const minutes = Math.floor(diffMs / (60 * 1000));
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const loadResumes = async () => {
    try {
      const r = await axiosInstance.get("/resumes");
      const items = (r.data && r.data.items) || [];
      const safeItems = items.length > 0 ? items : getDummyResumes();
      setResumes(safeItems);
      if (!selectedResumeId && safeItems.length > 0) {
        const active = safeItems.find((it) => it.is_active);
        setSelectedResumeId(active ? active.id : safeItems[0].id);
      }
    } catch (e) {
      setResumes(getDummyResumes());
    }
  };

  const loadAgentConfigs = async () => {
    try {
      const r = await axiosInstance.get("/job-agents");
      const apiItems = Array.isArray(r.data) ? r.data : [];
      setAgentConfigs(apiItems.map(item => ({
        ...item,
        id: item.id,
        jobsPerDay: item.jobs_per_day,
        resumeId: item.resume_id,
        resumeTitle: item.resume_title,
        jobDescription: item.job_description,
        applicationsCount: item.applications_count || 0,
        salary_range: item.salary_range,
        expected_salary: item.expected_salary,
        location_preference: item.location_preference,
        auto_apply: item.auto_apply,
        attached_files: item.attached_files,
      })));
    } catch (e) {
      setAgentConfigs([]);
    }
  };

  const loadHistory = async () => {
    try {
      const r = await axiosInstance.get("/agent-history");
      setHistory(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setHistory([]);
    }
  };

  useEffect(() => {
    trackPageView("/job-agent", "Job Agent");
    loadResumes();
    loadAgentConfigs();
    loadHistory();
    loadSubscription();
    setUserPlan(getUserPlan());
  }, []);

  const loadSubscription = async () => {
    try {
      const r = await axiosInstance.get("/subscription/status");
      setSubscription(r.data);
    } catch (e) {
      setSubscription(null);
    }
  };
  
  const handleFileUpload = (e) => {
      const files = e.target.files;
      if (files && files.length > 0) {
          const newFiles = Array.from(files).map(f => ({
              name: f.name,
              size: f.size,
              type: f.type,
              lastModified: f.lastModified
          }));
          setAttachedFiles([...attachedFiles, ...newFiles]);
          setSuccess("Files Attached");
          setTimeout(() => setSuccess(""), 2000);
      }
  };

  const removeAttachedFile = (index) => {
      const newFiles = [...attachedFiles];
      newFiles.splice(index, 1);
      setAttachedFiles(newFiles);
  };

  const handleStartAgent = async () => {
    setError("");
    setSuccess("");
    if (!selectedResumeId) return setError("Please select a CV");
    if (!jobDescription.trim()) return setError("Please provide target description");
    if (!jobTitle.trim()) return setError("Please provide target title");
    
    setLoading(true);
    trackButtonClick("Start Agent", "Job Agent");
    try {
      const resumeTitle = resumes.find((r) => r.id === selectedResumeId)?.title || "Selected CV";
      if (activeAgentId) {
        await axiosInstance.patch(`/job-agents/${activeAgentId}`, {
          title: jobTitle.trim(),
          location: location.trim() || null,
          jobs_per_day: jobsPerDay,
          resume_id: selectedResumeId,
          resume_title: resumeTitle,
          job_description: jobDescription || null,
          status: "running",
          salary_range: salaryRange || null,
          expected_salary: expectedSalary || null,
          location_preference: locationPreference || null,
          auto_apply: autoApply,
          attached_files: attachedFiles,
        });
      } else {
        const response = await axiosInstance.post("/job-agents", {
          title: jobTitle.trim(),
          location: location.trim() || null,
          jobs_per_day: jobsPerDay,
          resume_id: selectedResumeId,
          resume_title: resumeTitle,
          job_description: jobDescription || null,
          status: "running",
          salary_range: salaryRange || null,
          expected_salary: expectedSalary || null,
          location_preference: locationPreference || null,
          auto_apply: autoApply,
          attached_files: attachedFiles,
        });
        setActiveAgentId(response.data.id);
      }
      setIsCreatingNew(false);
      await loadAgentConfigs();
      setAgentStatus("running");
      setSuccess("Agent Activated");
      setTimeout(() => setSuccess(""), 3000);
      loadHistory();
    } catch (e) {
      setError(e?.response?.data?.detail || "Launch failed");
    } finally {
      setLoading(false);
    }
  };

  const handlePauseAgent = async () => {
    if (!activeAgentId) return;
    setLoading(true);
    try {
      await axiosInstance.patch(`/job-agents/${activeAgentId}`, { status: "paused" });
      setAgentStatus("paused");
      await loadAgentConfigs();
      setSuccess("Agent Dormant");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      setError("Failed to pause");
    } finally { setLoading(false); }
  };

  const handleResumeAgent = async () => {
    if (!activeAgentId) return;
    setLoading(true);
    try {
      await axiosInstance.patch(`/job-agents/${activeAgentId}`, { status: "running" });
      setAgentStatus("running");
      await loadAgentConfigs();
      setSuccess("Agent Re-Activated");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      setError("Failed to resume");
    } finally { setLoading(false); }
  };

  const handleSaveAgentConfig = async () => {
    if (!jobTitle.trim()) return setError("Target Title Required");
    // Limit check is handled by backend now
    
    setLoading(true);
    try {
      const resumeTitle = resumes.find((r) => r.id === selectedResumeId)?.title || "Selected CV";
      await axiosInstance.post("/job-agents", {
        title: jobTitle.trim(),
        location: location.trim() || null,
        jobs_per_day: jobsPerDay,
        resume_id: selectedResumeId,
        resume_title: resumeTitle,
        job_description: jobDescription || null,
        status: "idle",
        salary_range: salaryRange || null,
        expected_salary: expectedSalary || null,
        location_preference: locationPreference || null,
        auto_apply: autoApply,
        attached_files: attachedFiles,
      });
      await loadAgentConfigs();
      setIsCreatingNew(false);
      setSuccess("Configuration Saved");
      setTimeout(() => setSuccess(""), 2500);
    } catch (e) {
      setError("Save failed");
    } finally { setLoading(false); }
  };

  const getAgentStatusMeta = (status) => {
    if (status === "running") return { label: "Executing", icon: CircleDot, className: "bg-emerald-100 text-emerald-700 border-emerald-200" };
    if (status === "paused") return { label: "Dormant", icon: PauseCircle, className: "bg-amber-100 text-amber-700 border-amber-200" };
    if (status === "error") return { label: "Fault", icon: XCircle, className: "bg-red-100 text-red-700 border-red-200" };
    return { label: "Ready", icon: Circle, className: "bg-slate-100 text-slate-500 border-slate-200" };
  };

  return (
    <DashboardLayout activeTab="job-agent" setTab={(t) => navigate('/dashboard', { state: { tab: t } })}>
      <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20 font-sans">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 px-2">
           <div className="text-center md:text-left">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 tracking-tighter italic flex items-center justify-center md:justify-start gap-3">
                 <Zap size={28} className="text-blue-600 fill-blue-600 md:w-9 md:h-9" /> AI Job Agent
              </h1>
              <p className="mt-2 text-slate-500 font-medium flex items-center gap-2 text-xs sm:text-sm justify-center md:justify-start">
                <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
                System Online
              </p>
           </div>
           
           {/* Usage & Plan Badge */}
           <div className="flex items-center gap-4">
              {subscription && (
                <div className="flex items-center gap-3 px-5 py-3 bg-slate-50 border border-slate-200 rounded-2xl">
                   <div className="text-center">
                      <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Plan</div>
                      <div className="text-sm font-black text-blue-600 italic">{subscription.tier?.name || "Starter"}</div>
                   </div>
                   <div className="w-px h-8 bg-slate-200"></div>
                   <div className="text-center">
                      <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Agents</div>
                      <div className="text-sm font-black text-slate-900 italic">
                         {subscription.usage?.agents || 0} / {subscription.limits?.agents === -1 ? "∞" : (subscription.limits?.agents || 0)}
                      </div>
                   </div>
                   {subscription.trial?.active && (
                     <>
                       <div className="w-px h-8 bg-slate-200"></div>
                       <div className="text-center">
                          <div className="text-[9px] font-black text-emerald-500 uppercase tracking-widest">Trial</div>
                          <div className="text-sm font-black text-slate-900 italic">{subscription.trial.daysLeft}d left</div>
                       </div>
                     </>
                   )}
                </div>
              )}
              
              <button 
                onClick={() => {
                  setIsCreatingNew(true);
                  setActiveAgentId("");
                  setJobTitle("");
                  setLocation("");
                  setJobDescription("");
                  setSelectedResumeId("");
                  setJobsPerDay(15);
                  setSalaryRange("");
                  setExpectedSalary("");
                  setLocationPreference("");
                  setAutoApply(false);
                  setAttachedFiles([]);
                }}
                disabled={subscription && subscription.limits?.agents !== -1 && (subscription.usage?.agents || 0) >= (subscription.limits?.agents || 0)}
                className={`group flex items-center gap-4 bg-white p-2 pr-6 rounded-full border border-slate-200 shadow-sm hover:shadow-md transition-all ${
                  subscription && subscription.limits?.agents !== -1 && (subscription.usage?.agents || 0) >= (subscription.limits?.agents || 0)
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer"
                }`}
              >
                 <div className="w-12 h-12 flex items-center justify-center bg-slate-900 text-white rounded-full group-hover:bg-blue-600 transition-colors shadow-lg shadow-slate-900/20">
                    <Plus size={24} />
                 </div>
                 <div className="text-left">
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-tight">Create New</div>
                    <div className="text-sm font-black text-slate-900 italic group-hover:text-blue-600 transition-colors">Persona</div>
                 </div>
              </button>
           </div>
        </div>

        {/* Status Tabs */}
        <div className="flex items-center gap-4 px-2 mb-2">
            <button 
                onClick={() => setActiveTab("active")}
                className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest transition-all ${
                    activeTab === "active" 
                    ? "bg-slate-900 text-white shadow-lg shadow-slate-900/20" 
                    : "bg-white text-slate-400 hover:text-slate-600 border border-slate-200"
                }`}
            >
                Active Agents
            </button>
            <button 
                onClick={() => setActiveTab("archived")}
                className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest transition-all ${
                    activeTab === "archived" 
                    ? "bg-slate-900 text-white shadow-lg shadow-slate-900/20" 
                    : "bg-white text-slate-400 hover:text-slate-600 border border-slate-200"
                }`}
            >
                Archived
            </button>
        </div>

        {/* Top Active Agents Tabs */}
        <div className="relative">
           <div className="flex gap-4 overflow-x-auto pb-6 pt-2 px-2 scrollbar-hide snap-x">
              {agentConfigs
                .filter(a => activeTab === 'archived' ? a.status === 'archived' : a.status !== 'archived')
                .map(agent => {
                 const isActive = activeAgentId === agent.id;
                 const meta = getAgentStatusMeta(agent.status);
                 return (
                    <div 
                      key={agent.id}
                      onClick={() => handleSetActiveAgent(agent)}
                      className={`min-w-[85vw] sm:min-w-[300px] snap-center p-5 rounded-3xl border-2 cursor-pointer transition-all hover:scale-[1.02] active:scale-95 flex flex-col gap-3 group relative overflow-hidden bg-white ${
                        isActive 
                        ? 'border-blue-600 shadow-xl shadow-blue-500/10 ring-4 ring-blue-50' 
                        : 'border-slate-100 hover:border-blue-200 hover:shadow-lg'
                      }`}
                    >
                       <div className="flex items-center justify-between">
                          <div className={`p-2 rounded-xl transition-colors ${isActive ? 'bg-blue-100/50 text-blue-600' : 'bg-slate-50 text-slate-400 group-hover:text-blue-500'}`}>
                             <Target size={20} />
                          </div>
                          <div className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5 ${meta.className}`}>
                             <meta.icon size={10} /> {meta.label}
                          </div>
                       </div>
                       
                       <div>
                          <h4 className="font-black text-slate-900 text-lg leading-tight truncate px-1">{agent.title}</h4>
                          <div className="flex items-center gap-2 mt-1 px-1">
                             <MapPin size={10} className="text-slate-400"/>
                             <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider truncate">{agent.location || 'Global/Remote'}</span>
                          </div>
                       </div>
                       
                       {isActive && (
                         <div className="absolute top-0 right-0 p-3">
                           <span className="flex h-2.5 w-2.5 rounded-full bg-blue-600 shadow-lg shadow-blue-500/50"></span>
                         </div>
                       )}

                       {/* Archive Button */}
                       <button
                           onClick={(e) => handleArchiveAgent(e, agent.id)}
                           className="absolute top-2 right-2 p-2 bg-slate-100 rounded-full text-slate-400 hover:bg-red-50 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all z-10"
                           title="Archive Agent"
                       >
                           <Trash2 size={14} />
                       </button>
                    </div>
                 )
              })}
              
              {agentConfigs.length === 0 && (
                 <div className="w-full py-8 text-center border-2 border-dashed border-slate-200 rounded-3xl mx-2">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest italic">No Active Agents • Create One Above</p>
                 </div>
              )}
           </div>
        </div>

        {/* Horizontal Configuration Form */}
        <div className="bg-white border border-slate-200 rounded-[2rem] md:rounded-[2.5rem] p-5 md:p-10 shadow-xl shadow-slate-200/50 relative overflow-hidden mx-0 md:mx-2">
           <div className="absolute top-0 right-0 p-8 md:p-12 opacity-[0.03] pointer-events-none -rotate-12">
              <Zap size={150} className="md:w-[200px] md:h-[200px]" />
           </div>

           <div className="flex items-center gap-4 mb-8 relative z-10">
              <div className="p-3 bg-blue-50 rounded-2xl text-blue-600 border border-blue-100">
                 <Pencil size={24} />
              </div>
              <div>
                 <h3 className="text-xl font-black text-slate-900 tracking-tight italic">
                    {activeAgentId ? 'Agent Configuration' : 'New Agent Protocol'}
                 </h3>
                 <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                    {activeAgentId ? 'Modify active agent settings' : 'Define your job search criteria'}
                 </p>
              </div>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-12 gap-8 relative z-10">
              
              {/* Row 1: Core Params */}
              <div className="md:col-span-4 space-y-3">
                 <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Target Job Title</label>
                 <input 
                   className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-base md:text-sm font-bold outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all shadow-inner"
                   placeholder="e.g. Senior Frontend Dev"
                   value={jobTitle}
                   onChange={e => setJobTitle(e.target.value)}
                 />
              </div>

              <div className="md:col-span-4 space-y-3">
                 <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Search Location</label>
                 <input 
                   className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-base md:text-sm font-bold outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all shadow-inner"
                   placeholder="e.g. New York, Remote"
                   value={location}
                   onChange={e => setLocation(e.target.value)}
                 />
              </div>

              <div className="md:col-span-4 space-y-3">
                 <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Resume</label>
                 <div className="relative">
                   <select 
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all shadow-inner appearance-none cursor-pointer pr-10"
                      value={selectedResumeId}
                      onChange={e => setSelectedResumeId(e.target.value)}
                   >
                      <option value="">Select Resume...</option>
                      {resumes.map(r => <option key={r.id} value={r.id}>{r.title}</option>)}
                   </select>
                   <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                      <ChevronRight size={16} className="rotate-90"/>
                   </div>
                 </div>
              </div>
              
              {/* Row 2: Advanced Params (New) */}
              <div className="md:col-span-4 space-y-3">
                 <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Salary Range</label>
                 <input 
                   className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-base md:text-sm font-bold outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all shadow-inner"
                   placeholder="e.g. 100k - 150k"
                   value={salaryRange}
                   onChange={e => setSalaryRange(e.target.value)}
                 />
              </div>
              
              <div className="md:col-span-4 space-y-3">
                 <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Expected Salary</label>
                 <input 
                   className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-base md:text-sm font-bold outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all shadow-inner"
                   placeholder="e.g. 130000"
                   value={expectedSalary}
                   onChange={e => setExpectedSalary(e.target.value)}
                 />
              </div>

              <div className="md:col-span-4 space-y-3">
                 <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Location Preference</label>
                 <input 
                   className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-base md:text-sm font-bold outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all shadow-inner"
                   placeholder="e.g. Remote Only, Hybrid"
                   value={locationPreference}
                   onChange={e => setLocationPreference(e.target.value)}
                 />
              </div>


              {/* Row 3: Description & Attached Files */}
              <div className="md:col-span-6 space-y-3">
                 <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Job Description / Context</label>
                 <textarea 
                   className="w-full px-6 py-5 bg-slate-50 border border-slate-200 rounded-3xl text-sm font-medium leading-relaxed outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all shadow-inner min-h-[140px] resize-none"
                   placeholder="Paste target job description or keywords here..."
                   value={jobDescription}
                   onChange={e => setJobDescription(e.target.value)}
                 />
              </div>
              
              <div className="md:col-span-6 space-y-3">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Additional Files</label>
                <div className="w-full p-4 bg-slate-50 border border-slate-200 rounded-3xl min-h-[140px] flex flex-col">
                    <div className="flex-1 space-y-2 mb-4">
                        {attachedFiles.length === 0 && (
                            <div className="h-full flex flex-col items-center justify-center text-slate-400 opacity-60">
                                <File size={24} className="mb-2"/>
                                <span className="text-[10px] font-black uppercase tracking-widest">No files attached</span>
                            </div>
                        )}
                        {attachedFiles.map((file, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-white border border-slate-100 rounded-xl shadow-sm">
                                <div className="flex items-center gap-3 overflow-hidden">
                                    <div className="p-2 bg-blue-50 text-blue-600 rounded-lg"><FileText size={16}/></div>
                                    <span className="text-xs font-bold text-slate-700 truncate">{file.name}</span>
                                </div>
                                <button onClick={() => removeAttachedFile(idx)} className="text-slate-400 hover:text-red-500 transition-colors">
                                    <X size={16}/>
                                </button>
                            </div>
                        ))}
                    </div>
                    <label className="cursor-pointer flex items-center justify-center gap-2 w-full py-3 bg-white border-2 border-dashed border-slate-200 rounded-2xl text-slate-500 hover:border-blue-300 hover:text-blue-600 transition-all text-xs font-bold uppercase tracking-wider">
                        <Upload size={14} /> Add New File
                        <input type="file" className="hidden" multiple onChange={handleFileUpload} />
                    </label>
                </div>
              </div>


              {/* Row 4: Controls */}
              <div className="md:col-span-12 flex flex-col md:flex-row items-center gap-6 mt-2">
                 
                 {/* Auto Apply Toggle */}
                  <div className="flex items-center gap-4 bg-slate-50 p-4 pl-6 pr-8 rounded-3xl border border-slate-100 md:w-auto w-full justify-between">
                     <div className="flex flex-col">
                        <span className="text-sm font-black text-slate-900">Auto-Apply</span>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Autonomous Mode</span>
                     </div>
                     <button 
                         onClick={() => setAutoApply(!autoApply)}
                         className={`relative w-14 h-8 rounded-full transition-all duration-300 ${autoApply ? 'bg-blue-600' : 'bg-slate-200'}`}
                     >
                         <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow-sm transition-all duration-300 ${autoApply ? 'translate-x-6' : 'translate-x-0'}`}></div>
                     </button>
                  </div>

                 <div className="bg-slate-50 rounded-3xl p-4 md:px-6 border border-slate-100 flex-1 flex flex-col justify-center w-full">
                    <div className="flex justify-between items-center mb-2">
                       <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest hidden md:block">Daily Limit</label>
                       <span className="px-3 py-1 bg-white rounded-lg text-xs font-black text-blue-600 shadow-sm border border-slate-100">{jobsPerDay} apps/day</span>
                    </div>
                    <input 
                      type="range" 
                      min="10" 
                      max="30" 
                      value={jobsPerDay} 
                      onChange={e => setJobsPerDay(parseInt(e.target.value))} 
                      className="w-full h-2 bg-slate-200 rounded-full appearance-none accent-blue-600 cursor-pointer" 
                    />
                 </div>

                 <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                    <button 
                       onClick={activeAgent && activeAgent.status === 'running' ? handlePauseAgent : handleStartAgent}
                       disabled={loading}
                       className={`w-full sm:w-auto flex-1 md:flex-none px-8 py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2 ${
                          activeAgent && activeAgent.status === 'running' 
                          ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' 
                          : 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-500/25'
                       }`}
                    >
                       {loading ? <RefreshCw size={18} className="animate-spin"/> : (
                          activeAgent && activeAgent.status === 'running' 
                          ? <><Pause size={18} /> Pause Agent</> 
                          : <><Play size={18} /> {activeAgentId ? 'Update & Start' : 'Start Agent'}</>
                       )}
                    </button>
                    {!activeAgentId && (
                       <button 
                          onClick={handleSaveAgentConfig}
                          className="px-6 py-4 bg-white border-2 border-slate-200 text-slate-700 rounded-2xl hover:border-slate-300 transition-all active:scale-95 flex items-center justify-center"
                       >
                          <Save size={20}/>
                       </button>
                    )}
                 </div>
              </div>

           </div>
           
           {error && (
             <div className="mt-6 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 text-red-600 text-sm font-bold animate-pulse">
                <AlertCircle size={18} /> {error}
             </div>
           )}
           
           {success && (
             <div className="mt-6 p-4 bg-emerald-50 border border-emerald-100 rounded-2xl flex items-center gap-3 text-emerald-600 text-sm font-bold animate-in slide-in-from-bottom-2">
                <CheckCircle2 size={18} /> {success}
             </div>
           )}
        </div>

        {/* Intelligence Stream - Full Width */}
        <div className="bg-slate-900 rounded-[2.5rem] p-8 md:p-10 text-white shadow-2xl relative overflow-hidden mx-2 border border-slate-800">
           <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
              <div>
                 <h3 className="text-xl font-black text-white italic tracking-tighter flex items-center gap-3">
                    <TrendingUp size={24} className="text-blue-500" /> Live Application Activity
                 </h3>
                 <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">Real-time agent history</p>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 self-start md:self-auto">
                 <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                 <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Live</span>
              </div>
           </div>

           <div className="space-y-4">
              {/* Header Row - Only visible on desktop */}
              <div className="hidden md:flex items-center gap-4 px-6 py-3 text-[10px] font-black text-slate-500 uppercase tracking-widest border-b border-white/10 mb-2">
                 <div className="flex-[1.5]">Company & Position</div>
                 <div className="flex-1 text-center">Application Status</div>
                 <div className="flex-1 text-center">Protocol Links</div>
                 <div className="w-24 text-right">Activity</div>
              </div>

               {history.slice(0, 10).map((log) => (
                  <div key={log.id} className="flex flex-col md:flex-row md:items-center gap-4 p-4 md:p-5 rounded-3xl bg-white/5 border border-white/5 hover:bg-white/10 transition-all group relative overflow-hidden">
                     {/* Job Info */}
                     <div className="flex items-center gap-4 flex-[1.5] relative z-10">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-lg italic shadow-lg shrink-0 ${
                           log.status === 'applied' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20' : 
                           log.status === 'error' ? 'bg-rose-500/20 text-rose-400 border border-rose-500/20' : 
                           'bg-blue-500/20 text-blue-400 border border-blue-500/20'
                        }`}>
                           {log.company.charAt(0)}
                        </div>
                        <div className="min-w-0">
                           <div className="font-black text-white text-base truncate group-hover:text-blue-400 transition-colors">{log.job_title}</div>
                           <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider truncate">{log.company}</span>
                              <span className="w-1 h-1 rounded-full bg-slate-700" />
                              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider truncate">{log.location || 'Remote'}</span>
                           </div>
                        </div>
                     </div>
                     
                     {/* Status Badge */}
                     <div className="flex-1 flex justify-center relative z-10">
                        <div className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-2 border ${
                           log.status === 'applied' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 
                           log.status === 'error' ? 'bg-rose-500/10 text-rose-500 border-rose-500/20' : 
                           log.status === 'manual_apply' ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' :
                           'bg-blue-500/10 text-blue-400 border-blue-500/20'
                        }`}>
                           <div className={`w-1.5 h-1.5 rounded-full ${
                              log.status === 'applied' ? 'bg-emerald-500' : 
                              log.status === 'error' ? 'bg-rose-500' : 
                              log.status === 'manual_apply' ? 'bg-amber-500' :
                              'bg-blue-500'
                           }`} />
                           {log.status === 'applied' ? 'SUCCESS' : log.status.replace('_', ' ').toUpperCase()}
                        </div>
                     </div>

                     {/* Action Links */}
                     <div className="flex-1 flex items-center justify-center gap-3 relative z-10">
                        {log.job_url && (
                           <a 
                             href={log.job_url} 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-300 hover:bg-blue-600 hover:text-white transition-all border border-white/5"
                           >
                              <ExternalLink size={12} /> Job Link
                           </a>
                        )}
                        {log.manual_apply_url && (
                           <a 
                             href={log.manual_apply_url} 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className="flex items-center gap-2 px-3 py-1.5 bg-amber-500 text-black rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-amber-400 transition-all shadow-lg shadow-amber-500/20"
                           >
                              <Zap size={12} /> Manual Apply
                           </a>
                        )}
                        {!log.job_url && !log.manual_apply_url && (
                           <span className="text-[10px] font-bold text-slate-600 italic">No Links Available</span>
                        )}
                     </div>

                     {/* Relative Time */}
                     <div className="w-24 text-right relative z-10">
                        <div className="text-[10px] font-bold text-slate-400">{formatRelativeTime(log.created_at)}</div>
                        <div className="text-[8px] font-black text-slate-600 uppercase tracking-tighter">Recorded</div>
                     </div>
                  </div>
               ))}
               {history.length === 0 && (
                  <div className="py-20 text-center border border-dashed border-white/10 rounded-[2rem] bg-white/[0.02]">
                     <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-700">
                        <Cpu size={32} />
                     </div>
                     <p className="text-xs font-black text-slate-500 uppercase tracking-widest">Awaiting First Deployment</p>
                     <p className="text-[10px] font-bold text-slate-700 mt-2 uppercase tracking-tight">Active agents will relay their status here</p>
                  </div>
               )}
            </div>
        </div>

      </div>
    </DashboardLayout>
  );
};

export default JobAgent;
